// ABM List (Signals) — Excel lead import + table view
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, RefreshCw, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FilterSelect } from "@/components/common/FilterSelect";
import { signalsApi, outreachApi, downloadCustomLeadsTemplate } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { applyJourneyToAbmLead } from "@/lib/abmLead";
import { statusColor, flattenWorkflow } from "@/pages/signals/rnc/rncData";
import {
  SEARCH_DEBOUNCE_MS,
  getAbmEmptyMessage,
  resolveDebouncedQuery,
  shouldApplyLoadResponse,
} from "@/pages/signals/customLeadsSearch";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 10;

function formatImportFailure(result) {
  const errs = result?.errors;
  if (Array.isArray(errs) && errs.length > 0) {
    const first = errs[0];
    const rowPrefix = first.row ? `Row ${first.row}: ` : "";
    return `${rowPrefix}${first.message || "Validation failed"}`;
  }
  const fail = result?.failedCount || 0;
  if (fail > 0) {
    return `${fail} row${fail === 1 ? "" : "s"} failed validation`;
  }
  return "No leads imported";
}

function getBarColor(statusCol) {
  if (statusCol === "green") return "#16A34A";
  if (statusCol === "red") return "#DC2626";
  if (statusCol === "amber") return "#D97706";
  return "#94A3B8";
}

function _notifyImportResult(result) {
  const ok = result.successCount || 0;
  const fail = result.failedCount || 0;
  const skipped = result.skippedCount || 0;
  if (ok > 0 && fail === 0 && skipped === 0) {
    toast.success(`Imported ${ok} lead${ok === 1 ? "" : "s"}`);
  } else if (ok > 0) {
    toast.message(
      `Imported ${ok} lead(s)`
      + (skipped ? `; ${skipped} duplicate(s) skipped` : "")
      + (fail ? `; ${fail} failed validation` : "")
    );
  } else if (skipped > 0 && fail === 0) {
    toast.message(`No new leads — ${skipped} duplicate(s) skipped`);
  } else if (fail > 0) {
    toast.error(formatImportFailure(result));
  } else {
    toast.error("No leads imported");
  }
}



function getStepLabel(lead, flatSteps, workflowAssigned) {
  if (lead.currentStep) return lead.currentStep;
  if (workflowAssigned && flatSteps?.[lead.stepIndex]?.title) {
    return flatSteps[lead.stepIndex].title;
  }
  if (!workflowAssigned) return "Pending Workflow Assignment";
  return "N/A";
}

function LeadsTable({
  loading,
  refreshing,
  error,
  leads,
  emptyMessage,
  deletingId,
  onDelete,
  onViewDetails,
}) {
  const showBlockingLoad = loading && leads.length === 0;
  return (
    <div
      className={`card rnc-table-card${refreshing ? " is-refreshing" : ""}`}
      data-testid="custom-leads-table"
      aria-busy={refreshing || undefined}
    >
      <p className="rnc-table-scroll-hint" aria-hidden="true">Swipe table to see more columns</p>
      <div className="rnc-table-wrap">
        <table className="rnc-data-table abm-leads-table">
          <thead>
            <tr>
              <th>COMPANY</th>
              <th>CAMPAIGN</th>
              <th>CURRENT STEP</th>
              <th>STATUS</th>
              <th>PROGRESS</th>
              <th>LAST ACTIVITY</th>
              <th>IMPORTED</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {showBlockingLoad && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "var(--slate-400)" }}>
                  Loading ABM List leads…
                </td>
              </tr>
            )}
            {!showBlockingLoad && error && leads.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "#b91c1c" }}>
                  {error}
                </td>
              </tr>
            )}
            {!showBlockingLoad && !error && leads.map((lead) => {
              const sc = lead.status ? statusColor(lead.status) : "slate";
              const barColor = getBarColor(sc);
              const workflowAssigned = Boolean(lead.workflowAssigned);
              const flat = flattenWorkflow(lead.signalWorkflow);
              const curStepLabel = getStepLabel(lead, flat, workflowAssigned);
              const progressPct = lead.progress == null ? null : Math.round(Number(lead.progress) * 100);
              const isDeleting = deletingId === lead.id;
              return (
                <tr key={lead.id} data-testid={`custom-lead-row-${lead.id}`}>
                  <td>
                    <div className="rowcompany">
                      <div className="mk" style={{ background: lead.color }}>
                        {lead.initials}
                      </div>
                      <div>
                        <div className="nm">{lead.company}</div>
                        <div className="sub">{lead.location || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize: 13 }}>{lead.campaignName || "N/A"}</td>
                  <td style={{ fontSize: 13 }}>{curStepLabel}</td>
                  <td>
                    {lead.status ? (
                      <span className={`status-pill badge ${sc}`}><span className="dotb" />{lead.status}</span>
                    ) : (
                      <span style={{ color: "var(--slate-400)", fontSize: 13 }}>N/A</span>
                    )}
                  </td>
                  <td>
                    {progressPct == null ? (
                      <span style={{ color: "var(--slate-400)", fontSize: 13 }}>N/A</span>
                    ) : (
                      <div className="progress-cell">
                        <div className="bar"><div style={{ width: `${progressPct}%`, background: barColor }} /></div>
                        <span>{progressPct}%</span>
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 13 }}>{formatDateTime(lead.lastActivity, "N/A")}</td>
                  <td style={{ fontSize: 13 }}>{formatDateTime(lead.dateAdded || lead.uploadedAt)}</td>
                  <td>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "flex-start" }}>
                      <button
                        type="button"
                        className="view-details-btn"
                        onClick={() => onViewDetails(lead.id)}
                        data-testid={`view-abm-${lead.id}`}
                      >
                        View Details
                      </button>
                      <button
                        type="button"
                        className="btn"
                        title="Delete lead"
                        disabled={isDeleting}
                        onClick={() => onDelete(lead)}
                        data-testid={`custom-lead-delete-${lead.id}`}
                        aria-label={`Delete ${lead.contact || lead.company}`}
                        style={{ padding: "4px 8px", color: "#b91c1c", borderColor: "transparent" }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!showBlockingLoad && !refreshing && !error && leads.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 40, color: "var(--slate-400)" }}>
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

LeadsTable.propTypes = {
  loading: PropTypes.bool,
  refreshing: PropTypes.bool,
  error: PropTypes.string,
  leads: PropTypes.array,
  emptyMessage: PropTypes.string,
  deletingId: PropTypes.string,
  onDelete: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
};

export default function CustomLeadsPanel({ refreshToken = 0 }) {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const queryRef = useRef(query);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [leads, setLeads] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [total, setTotal] = useState(0);
  const [imports, setImports] = useState([]);
  const [lastSummary, setLastSummary] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const loadRequestIdRef = useRef(0);
  const searchDebounceRef = useRef(null);

  queryRef.current = query;

  const commitSearchQuery = useCallback((rawValue) => {
    const next = resolveDebouncedQuery(rawValue, queryRef.current);
    if (next === null) return;
    queryRef.current = next;
    setQuery(next);
    setPage(0);
  }, []);

  const flushSearch = useCallback((rawValue = search) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    commitSearchQuery(rawValue);
  }, [commitSearchQuery, search]);

  useEffect(() => {
    searchDebounceRef.current = setTimeout(() => {
      commitSearchQuery(search);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [search, commitSearchQuery]);

async function _fetchJourneysForLeads(rawLeads) {
  const leadIds = rawLeads.map((l) => l.id).filter(Boolean);
  if (!leadIds.length) return {};
  try {
    const batch = await outreachApi.listAbmJourneys(leadIds);
    return batch?.journeys || {};
  } catch {
    return {};
  }
}

  const fetchLeads = useCallback(async (fetchQuery, fetchPage, fetchPageSize) => {
    const requestId = ++loadRequestIdRef.current;
    const isInitialLoad = !hasLoaded;

    if (isInitialLoad) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    if (isInitialLoad) setError(null);

    try {
      const data = await signalsApi.listCustomLeads({
        q: fetchQuery || undefined,
        limit: fetchPageSize,
        offset: fetchPage * fetchPageSize,
      });
      if (!shouldApplyLoadResponse(requestId, loadRequestIdRef.current)) return;

      const rawLeads = data.leads || [];
      const journeys = await _fetchJourneysForLeads(rawLeads);
      const enriched = rawLeads.map((lead, index) => (
        applyJourneyToAbmLead(lead, journeys[lead.id], index)
      ));

      setLeads(enriched);
      setTotal(data.total || 0);
      setImports(data.imports || []);
      setError(null);
      setHasLoaded(true);
    } catch (e) {
      if (!shouldApplyLoadResponse(requestId, loadRequestIdRef.current)) return;

      const message = e.message || "Could not load ABM List leads";
      if (isInitialLoad) {
        setError(message);
        setLeads([]);
        setTotal(0);
      } else {
        toast.error(message);
      }
    } finally {
      if (shouldApplyLoadResponse(requestId, loadRequestIdRef.current)) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [hasLoaded]);

  const load = useCallback(() => fetchLeads(query, page, pageSize), [fetchLeads, query, page, pageSize]);

  useEffect(() => {
    fetchLeads(query, page, pageSize);
  }, [fetchLeads, query, page, pageSize, refreshToken]);

  const emptyMessage = getAbmEmptyMessage({
    hasLoaded,
    query,
    hasAnyLeadsInWorkspace: total > 0,
  });

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const rangeFrom = total === 0 ? 0 : page * pageSize + 1;
  const rangeTo = page * pageSize + leads.length;
  const onPageSizeChange = (nextRaw) => {
    const next = Number(nextRaw);
    if (!PAGE_SIZE_OPTIONS.includes(next)) return;
    setPageSize(next);
    setPage(0);
  };

  const handleDownloadTemplate = async () => {
    try {
      await downloadCustomLeadsTemplate();
      toast.success("Template downloaded");
    } catch (e) {
      toast.error(e.message || "Failed to download template");
    }
  };

  const handleDeleteLead = (lead) => {
    setDeleteTarget(lead);
  };

  const confirmDeleteLead = async () => {
    if (!deleteTarget) return;
    const lead = deleteTarget;
    setDeletingId(lead.id);
    try {
      await signalsApi.deleteCustomLead(lead.id);
      toast.success("Lead removed");
      setDeleteTarget(null);
      if (leads.length === 1 && page > 0) {
        setPage((p) => Math.max(0, p - 1));
      } else {
        await load();
      }
    } catch (e) {
      toast.error(e.message || "Could not delete lead");
    } finally {
      setDeletingId(null);
    }
  };

  const deleteTargetLabel = deleteTarget
    ? (deleteTarget.contact || deleteTarget.company || deleteTarget.contactName || deleteTarget.companyName || "this lead")
    : "";

  const handleUpload = async (file) => {
    if (!file) return;
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".xlsx") && !lower.endsWith(".xls")) {
      toast.error("Only .xlsx and .xls files are supported");
      return;
    }
    setUploading(true);
    try {
      const result = await signalsApi.importCustomLeads(file);
      setLastSummary(result);
      _notifyImportResult(result);
      setPage(0);
      await load();
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const recentImports = useMemo(() => imports.slice(0, 5), [imports]);

  return (
    <div className="space-y-3" data-testid="signals-custom-panel">
      <div className="filters" style={{ marginBottom: 0, flexWrap: "wrap" }}>
        <input
          placeholder="Search company, contact, email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") flushSearch(search);
          }}
          data-testid="custom-leads-search"
        />
        <button
          type="button"
          className="btn"
          onClick={handleDownloadTemplate}
          data-testid="custom-leads-download-template"
        >
          <Download size={14} /> Download Template
        </button>
        <button
          type="button"
          className="btn"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          data-testid="custom-leads-upload-btn"
        >
          <Upload size={14} /> {uploading ? "Importing…" : "Upload Excel"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          style={{ display: "none" }}
          data-testid="custom-leads-file-input"
          onChange={(e) => handleUpload(e.target.files?.[0])}
        />
        <button
          type="button"
          className="btn"
          onClick={() => load()}
          data-testid="custom-leads-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {lastSummary && (
        <div
          className="card"
          style={{ padding: "12px 16px", fontSize: 13.5 }}
          data-testid="custom-leads-import-summary"
        >
          <strong>Last import:</strong>{" "}
          {lastSummary.fileName}
          {" · "}
          {lastSummary.successCount} imported
          {lastSummary.duplicateFileCount
            ? ` · ${lastSummary.duplicateFileCount} duplicate(s) in file`
            : ""}
          {lastSummary.duplicateDbCount
            ? ` · ${lastSummary.duplicateDbCount} already in workspace`
            : ""}
          {lastSummary.failedCount ? ` · ${lastSummary.failedCount} failed validation` : null}
          {Boolean(lastSummary.skippedCount) && ` · ${lastSummary.skippedCount} skipped`}
          {" · "}
          {lastSummary.totalRows} rows processed
        </div>
      )}

      <LeadsTable
        loading={loading}
        refreshing={refreshing}
        error={error}
        leads={leads}
        emptyMessage={emptyMessage}
        deletingId={deletingId}
        onDelete={handleDeleteLead}
        onViewDetails={(leadId) => navigate(`/signals/abm/${encodeURIComponent(leadId)}`)}
      />

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }} className="custom-leads-pagination">
        <p style={{ color: "var(--slate-500)", fontSize: 13.5, margin: 0 }}>
          Showing {rangeFrom}{"–"}{rangeTo} of {total} ABM List leads
        </p>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--slate-500)" }}>
            Per page
            <FilterSelect
              variant="field"
              value={pageSize}
              onChange={onPageSizeChange}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: String(n) }))}
              className="w-auto"
              triggerClassName="h-9 w-auto min-w-[52px] px-2.5"
              data-testid="custom-leads-page-size"
              aria-label="Records per page"
            />
          </label>
          <button
            type="button"
            className="btn"
            disabled={page <= 0 || loading || refreshing}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            data-testid="custom-leads-page-prev"
          >
            Previous
          </button>
          <span style={{ fontSize: 13, color: "var(--slate-500)", alignSelf: "center" }}>
            Page {Math.min(page + 1, pageCount)} / {pageCount}
          </span>
          <button
            type="button"
            className="btn"
            disabled={loading || refreshing || page + 1 >= pageCount || total === 0}
            onClick={() => setPage((p) => p + 1)}
            data-testid="custom-leads-page-next"
          >
            Next
          </button>
        </div>
      </div>

      {recentImports.length > 0 && (
        <div className="card" style={{ padding: "12px 16px" }} data-testid="custom-leads-import-history">
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--slate-500)", marginBottom: 8 }}>
            Recent imports
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--slate-600)" }}>
            {recentImports.map((imp) => (
              <li key={imp.id}>
                {imp.fileName} — {imp.successCount} imported
                {imp.duplicateFileCount || imp.duplicateDbCount
                  ? `, ${(imp.duplicateFileCount || 0) + (imp.duplicateDbCount || 0)} skipped duplicates`
                  : ""}
                {imp.failedCount ? `, ${imp.failedCount} failed` : ""}
                {" · "}
                {formatDateTime(imp.createdAt)}
              </li>
            ))}
          </ul>
        </div>
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deletingId) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent data-testid="abm-delete-lead-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from ABM List?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTargetLabel
                ? `Remove ${deleteTargetLabel} from the ABM List?`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={Boolean(deletingId)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              disabled={Boolean(deletingId)}
              onClick={(e) => {
                e.preventDefault();
                confirmDeleteLead();
              }}
            >
              {deletingId ? "Removing…" : "Remove lead"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

CustomLeadsPanel.propTypes = {
  refreshToken: PropTypes.number,
};
